import { useRouteLoaderData } from 'react-router'
// `import type` (not an inline `{ type … }`) so Node's type-stripping fully elides
// this statement — `prisma/seed.prod.ts` reaches `user.ts` via `reconcileRbac` and
// runs under plain `node`, which cannot load the `.tsx` this only needs for a type.
import type { loader as rootLoader } from '#app/root.tsx'

function isUser(
	user: any,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
	return user && typeof user === 'object' && typeof user.id === 'string'
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

// The RBAC vocabulary. These value-level registries are the single source of
// truth for what actions/entities/access levels exist; the `Permission` rows in
// the database (and the seed) must mirror them. The types are derived from the
// arrays so the type and the runtime list cannot drift.
export const permissionActions = ['create', 'read', 'update', 'delete'] as const
export const permissionEntities = [
	'user',
	'post',
	'audit',
	'role',
	'impersonation',
	'contactMessage',
	'integrationLog',
] as const
// The access dimension is `:any`-only since ADR-085: `:own` was seeded,
// granted, and rendered as toggles, but no guard ever requested it — its one
// real consumer (the Notes delete guard, ADR-061) left with the Notes domain
// (ADR-050). Self-account editing is authentication-gated (`requireUserId`),
// never permission-gated. An entity that genuinely needs owner-scoped guards
// reintroduces `'own'` here and in `entityAccesses` — the matrix machinery
// below is access-generic.
export const permissionAccesses = ['any'] as const

export type PermissionAction = (typeof permissionActions)[number]
export type PermissionEntity = (typeof permissionEntities)[number]
export type PermissionAccess = (typeof permissionAccesses)[number]

// Two named privilege registries beside the catalog above. They look similar but
// mean different things — the grill (ADR-083) kept them apart on purpose: a
// different set, a different quantifier, a different consumer. Both are stated as
// their full ADR-069 intent and intersected against the live `permissionEntities`
// catalog by their consumers, so an entity named here before it lands in the
// catalog simply stays dormant until it does (see `admin-floor.ts`).

/**
 * **Floor-critical** entities: a capable admin must hold `:any` on **every** one
 * (`every` quantifier). This is the admin-floor invariant (ADR-069) — the set the
 * data layer refuses to let the last capable admin drop below — and also what
 * makes a single grant load-bearing (`isFloorCritical`, `rbac-admin.server.ts`).
 */
export const floorCriticalEntities = ['user', 'role'] as const

/**
 * **Management** entities: a role granting `:any` on **any** one of them
 * (`some` quantifier) can manage other people's access, which is what marks it a
 * "privileged" / management-granting role for the elevated chip (`isPrivilegedRole`,
 * `user-admin.server.ts`). A different set and quantifier from the floor above.
 */
export const managementEntities = ['user', 'audit'] as const

// Which access levels each entity is scoped by (ADR-065). Every entity is
// `:any`-only (ADR-085): holding a permission means admin-grade reach over
// *other people's* rows, so regular users hold no permissions at all (see
// `roleGrantedAccess`) — readers only (ADR-050).
export const entityAccesses = {
	// A user's *own* account is managed under authentication, not permission
	// (ADR-085): `*:user:any` is the admin reach over other accounts, and there
	// is no `user:own`.
	user: ['any'],
	post: ['any'],
	// The audit log is an admin-only investigation surface (ADR-070): never
	// owner-scoped, so it carries `any` alone (`read:audit:any` for the viewer).
	audit: ['any'],
	// Roles are editable RBAC data (ADR-069): you manage *the* roles or none, so
	// the entity is never owner-scoped — `any` alone, generating `*:role:any`.
	// This is also the second half of the admin-floor capability (`role:any`),
	// which `admin-floor.ts` picks up automatically now that `role` is a catalog
	// entity.
	role: ['any'],
	// Impersonation is an admin support tool: you impersonate *other* users or
	// no one, so the entity is any-only (`create:impersonation:any` is the grant
	// the guard checks). Deliberately NOT an admin-floor capability — see
	// `admin-floor.ts`.
	impersonation: ['any'],
	// Contact Messages are inbound visitor PII triaged in an admin inbox (EPT-143):
	// you read/handle *all* messages or none, never your own, so the entity is
	// any-only (`read`/`update`/`delete:contactMessage:any`). Not an admin-floor
	// capability — losing it strands the inbox, not the admin bootstrap.
	contactMessage: ['any'],
	// The Integration Log is an admin-only operations surface (ADR-080): you read
	// *all* traces or none, never your own, so the entity is any-only
	// (`read:integrationLog:any` gates the viewer and scopes the failure-alert
	// audience). Not an admin-floor capability — losing it strands the log
	// viewer, not the admin bootstrap.
	integrationLog: ['any'],
} as const satisfies Record<PermissionEntity, ReadonlyArray<PermissionAccess>>

// The set of Roles a user can hold, named once here (the database `Role` rows
// must mirror it). Guards take a `RoleName`, so a mistyped role is a compile
// error rather than a silent 403 at runtime.
export const roleNames = ['user', 'admin'] as const
export type RoleName = (typeof roleNames)[number]

// The access level each Role is granted across every entity/action, or `null`
// for no catalog-derived grants. Exhaustive over RoleName (a role without an
// entry is a compile error). Admin holds every `:any` permission; the `user`
// role holds none (ADR-085) — it is the plain-member marker, not a grant
// bundle, because self-account editing is authentication-gated.
export const roleGrantedAccess = {
	admin: 'any',
	user: null,
} satisfies Record<RoleName, PermissionAccess | null>

// The full permission matrix derived from the vocabulary: every action on every
// entity, at each access level that entity is scoped by (`entityAccesses`). The
// database `Permission` rows are exactly this set, so the seed generates them
// from here rather than from a hand-maintained list.
export function getPermissionMatrix(): Array<{
	action: PermissionAction
	entity: PermissionEntity
	access: PermissionAccess
}> {
	return permissionEntities.flatMap((entity) =>
		permissionActions.flatMap((action) =>
			entityAccesses[entity].map((access) => ({ action, entity, access })),
		),
	)
}

// An access segment of a PermissionString names the stored access level the
// requirement is pinned to. The comma-joined "any of these" set form left with
// `:own` (ADR-085) — a single access level makes it name nothing.
type AccessSegment = PermissionAccess

export type PermissionString =
	| `${PermissionAction}:${PermissionEntity}`
	| `${PermissionAction}:${PermissionEntity}:${AccessSegment}`

// What a guard requires: an entity+action, and optionally the set of access
// levels that satisfy it (undefined = any access).
export type RequiredPermission = {
	action: PermissionAction
	entity: PermissionEntity
	access?: Array<PermissionAccess>
}

export function parsePermissionString(
	permissionString: PermissionString,
): RequiredPermission {
	const [action, entity, access] = permissionString.split(':') as [
		PermissionAction,
		PermissionEntity,
		AccessSegment | undefined,
	]
	return {
		action,
		entity,
		access: access ? [access] : undefined,
	}
}

// A single permission granted to a user, as stored on a Role. Only the three
// columns the match rule reads are required, so any query selecting them — the
// root loader's user, or the server guard's lookup — satisfies this shape.
type GrantedPermission = { action: string; entity: string; access: string }
type UserWithPermissions = {
	roles: Array<{ permissions: Array<GrantedPermission> }>
}

// The one home of the permission-match rule. A granted permission satisfies a
// requirement when it is on the same entity+action and its access is among those
// required (or the requirement names no access). Both the server guard
// (`requireUserWithPermission`) and the client `userHasPermission` go through
// this, so the rule cannot diverge between the two.
export function permissionSatisfies(
	granted: GrantedPermission,
	required: RequiredPermission,
): boolean {
	return (
		granted.entity === required.entity &&
		granted.action === required.action &&
		(!required.access ||
			required.access.includes(granted.access as PermissionAccess))
	)
}

export function userHasPermission(
	user: UserWithPermissions | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const required = parsePermissionString(permission)
	return user.roles.some((role) =>
		role.permissions.some((granted) => permissionSatisfies(granted, required)),
	)
}

/**
 * The identity string for a user on *private* surfaces: name → email, the
 * first present (EPT-145). The one home of the fallback rule — chrome, admin
 * surfaces, and the audit label (`labelForActor`) all delegate here. Public
 * surfaces (blog byline, feed) deliberately do NOT use it: they fall back to a
 * neutral literal so an author's email never renders publicly.
 */
export function displayName(user: { name?: string | null; email: string }) {
	return user.name ?? user.email
}

export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: RoleName,
) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}
